import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Radio,
  Pagination,
  Drawer,
  ThemeProvider,
  createTheme,
  IconButton,
  CircularProgress,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "@fontsource/manrope";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const ConfluenceSpacesComponentPage = ({
  issueType,
  taskType,
  spacesLoading,
  setConfluencePageOpen,
  spacesList,
  selectedSpace,
  setSelectedSpace,
  onSpaceSelected,
  setPageTitleModalOpen,
  setPreviousSelection,
  setExistingChoice,
  setWorkspaceChoice,
  setPageTitle,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [space, setSpace] = useState(selectedSpace);
  const [saveLoading, setSaveLoading] = useState(false);

  // const spacesPerPage = 8;

  const handleRadioChange = (space) => {
    setSpace(space);
  };


  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setConfluencePageOpen(false);
  };

  const handleOk = () => {
    setSelectedSpace(space);
    if (onSpaceSelected) onSpaceSelected(space);
  };

  const filteredSpaces = spacesList.filter((space) =>
    space.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // const displayedSpaces = filteredSpaces.slice(
  //   (page - 1) * spacesPerPage,
  //   page * spacesPerPage
  // );

  const handleBack = () => {
    if (taskType === "Import") {
      setSelectedSpace(null);
      setPreviousSelection(null);
    } else {
      setPageTitleModalOpen(true);
      setExistingChoice("");
      setWorkspaceChoice("");
      setPageTitle("");
      setIsDrawerOpen(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={handleCloseDrawer}
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "90vw", // Mobile
              sm: "70vw", // Tablet
              md: "50vw", // Small laptops
              lg: "40vw", // Desktop
            },
            maxWidth: "none",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ArrowBackIosNewIcon
                fontSize="small"
                onClick={handleBack}
                sx={{ mr: 1, cursor: "pointer" }}
              />
              <Typography variant="h6">
                {taskType} {issueType}
              </Typography>
            </Box>

            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {/* <Typography variant="subtitle2" color="text.secondary">
              Choose a space to continue
            </Typography> */}
            <TextField
              size="small"
              placeholder="Search spaces"
              disabled={spacesList.length === 0}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              fullWidth
              // sx={{
              //   width: "100%", // responsive widths
              // }}
            />
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 1, // add padding so scrollbar doesn’t overlap
          }}
        >
          {spacesList.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              {spacesLoading ? (
                <CircularProgress />
              ) : (
                <Typography>No spaces found</Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 1.5,
              }}
            >
              {filteredSpaces.map((s) => (
                <Card
                  key={s.key}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: 90,
                    p: 1,
                    borderRadius: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => handleRadioChange(s)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", ml: -1 }}>
                    <Radio
                      checked={space?.key === s.key}
                      value={s.key}
                      name="confluence-space-radio"
                      onChange={() => handleRadioChange(s)}
                    />
                    <Box>
                      <Typography variant="body2">{s.name}</Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Footer Buttons */}
        <Box
          sx={{
            flexShrink: 0,
            pt: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#fff",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleCloseDrawer}
            sx={{
              width: { xs: "100%", sm: "60px", md: "80px" },
              textTransform: "none",
              borderRadius: "8px",
              borderWidth: "2px",
              borderColor: "#0094FB",
              "&:hover": { backgroundColor: "#ffffff" },
              color: "#0094FB",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleOk}
            disabled={spacesLoading || !space}
            sx={{
              width: { xs: "100%", sm: "60px", md: "80px" },
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: "#0094FB",
              "&:hover": { backgroundColor: "#0094FB" },
              "&.Mui-disabled": {
                backgroundColor: "#0094FB",
                color: "#fff",
                opacity: 0.6,
              },
            }}
          >
            {saveLoading ? <CircularProgress size={20} /> : "Confirm"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConfluenceSpacesComponentPage;
